import PDF from "./PDF";
import { INVOICE_SETTINGS } from "../classes/Invoice";
import { i18n } from "../boot/i18n";
import { router } from "../router";
import { ORIGIN } from "../constants";
import { formatCurrency } from "../util/misc";

import { cloneDeep, defaults, forEach, get } from "lodash";
import { format } from "date-fns";

// Rows in the payments block at the foot of the totals table — Early Payment
// Discount, Fees, Payment(s), Finance Charge, then Balance — or 0 when the
// block is not drawn at all. The block appears once the invoice carries a
// payment OR an accrued late-payment finance charge (either changes what the
// customer still owes), and the table layout uses the count to place the
// separator rule above the block. Exported for the unit spec.
export function paymentsBlockRowCount(total) {
  if (!total || !(total.payments || total.financeCharge)) {
    return 0;
  }
  return (
    1 +
    Number(Boolean(total.addDiscount)) +
    Number(Boolean(total.fee)) +
    Number(Boolean(total.payments)) +
    Number(Boolean(total.financeCharge))
  );
}

export default class InvoicePDF extends PDF {
  constructor(options) {
    super(options);
  }

  getCategories(invoice) {
    if (!invoice.items || !invoice.items.length) {
      return null;
    }
    let categories = {};
    invoice.items.forEach((item, i) => {
      const category = item.category || i18n.global.t("Other");
      if (!(category in categories)) {
        categories[category] = [];
      }
      categories[category].push(item);
    });
    if (
      Object.keys(categories).length > 1 ||
      Object.keys(categories)[0] !== i18n.global.t("Other")
    ) {
      return categories;
    }
    return null;
  }

  async drawInvoice(invoice, options) {
    if (options) {
      defaults(options, INVOICE_SETTINGS);
    } else {
      options = cloneDeep(INVOICE_SETTINGS);
    }

    // Fetch images
    this.doc.images = {};
    const fetches = [];
    invoice.items.forEach((item, i) => {
      if (item.image) {
        fetches.push(
          this.fetchImage(item.image.src).then((data) => {
            this.doc.images[item.id] = data;
          })
        );
      }
    });
    await Promise.all(fetches);

    const meta = {
      title: i18n.global.tc("Invoice"),
      subtitle: [
        {
          text: invoice.id,
          style: "id",
        },
        {
          text: [
            {
              text: i18n.global.tc("Sale").toUpperCase() + ": ",
              style: "bold",
            },
            {
              text: invoice.projectID + invoice.saleID,
              style: "id",
            },
          ],
        },
        {
          text: [
            { text: i18n.global.t("Date").toUpperCase() + ": ", style: "bold" },
            format(invoice.invoiceDate, i18n.global.t("format.date-short")),
          ],
        },
        {
          text: [
            { text: i18n.global.t("Due").toUpperCase() + ": ", style: "bold" },
            format(invoice.dueDate, i18n.global.t("format.date-short")),
          ],
        },
        {
          text: [
            {
              text: i18n.global.t("Terms").toUpperCase() + ": ",
              style: "bold",
            },
            invoice.terms.description,
          ],
        },
      ],
      qr:
        ORIGIN +
        router.resolve({
          name: "customer-invoice",
          params: {
            customerID: invoice.data.billTo.address.contact,
            invoiceID: invoice.id,
          },
        }).fullPath,
    };

    const addresses = [
      { title: i18n.global.t("Bill To"), ...invoice.billTo.address.data },
      { title: i18n.global.t("Ship To"), ...invoice.shipTo.address.data },
    ];

    await this.header({
      location: invoice.location,
      meta,
      addresses,
    });
    let y = this.page.fold;

    // Totals
    const paymentsBlockRows = paymentsBlockRowCount(invoice.data.total);
    const getTotal = (key, styles = []) => {
      const total = get(invoice.total, key);
      const style = ["bold", "right", ...styles];
      let text = total;
      if (styles && styles.includes("negative")) {
        text = "−" + text;
      }
      return { text, style };
    };

    const categories = this.getCategories(invoice);
    const categoryCount = categories ? Object.keys(categories).length : 0;
    const totals = {
      table: {
        body: [],
      },
      style: { fontSize: 9.6 },
      layout: {
        hLineWidth: (i, node) => {
          if (
            (categoryCount && i === categoryCount) ||
            i ===
              categoryCount + 2 + 1 * Boolean(invoice.data.total.discount) ||
            (paymentsBlockRows &&
              i === node.table.body.length - 1 - paymentsBlockRows)
          ) {
            return 0.5;
          }
          if (i === node.table.body.length - 1) {
            return 2;
          }
          return 0;
        },
        vLineWidth: () => 0,
        vLineColor: () => this.borderColor,
        hLineColor: () => this.borderColor,
        paddingLeft: () => 12,
        paddingRight: () => 12,
        paddingTop: (i, node) => {
          if (
            (categoryCount && i === categoryCount) ||
            i ===
              categoryCount + 2 + 1 * Boolean(invoice.data.total.discount) ||
            (paymentsBlockRows &&
              i === node.table.body.length - 1 - paymentsBlockRows)
          ) {
            return 3.5;
          }
          if (i === node.table.body.length - 1) {
            return 4.5;
          }
          return 2;
        },
      },
    };
    if (invoice.total.categories) {
      invoice.sortedCategories.forEach((category) => {
        totals.table.body.push([category, getTotal("categories." + category)]);
      });
    }
    totals.table.body.push([i18n.global.t("Subtotal"), getTotal("subtotal")]);
    if (invoice.data.total.discount) {
      totals.table.body.push([
        { text: i18n.global.t("Discount"), noWrap: true },
        getTotal("discount", ["negative"]),
      ]);
    }
    // Round tax to avoid floating-point precision display errors
    const taxPercent = invoice.notax ? 0 : Math.round(invoice.tax * 100) / 100;
    totals.table.body.push([
      {
        text: i18n.global.t("Sales Tax") + ` (${taxPercent}%)`,
        noWrap: true,
      },
      getTotal("tax"),
    ]);
    totals.table.body.push([i18n.global.tc("Total"), getTotal("price")]);
    if (invoice.data.total.downPayment) {
      if (invoice.data.total.downPaymentFee) {
        totals.table.body.push([
          {
            text: i18n.global.t("Fees"),
            noWrap: true,
          },
          getTotal("downPaymentFee"),
        ]);
      }
      totals.table.body.push(
        [
          {
            text:
              i18n.global.t("Down Payment") +
              (invoice.total.data.downPaymentPercent < 100
                ? ` (${invoice.total.downPaymentPercent})`
                : ""),
            noWrap: true,
          },
          getTotal("downPayment", ["negative"]),
        ],
        [
          {
            text: i18n.global.t("Due"),
            style: paymentsBlockRows ? {} : { fontSize: 11.5 },
          },
          getTotal("due"),
        ]
      );
    }
    if (paymentsBlockRows) {
      if (invoice.data.total.addDiscount) {
        totals.table.body.push([
          {
            text:
              i18n.global.t("Early Payment Discount") +
              ` (${invoice.total.addDiscountPercent})`,
            noWrap: true,
          },
          getTotal("addDiscount", ["negative"]),
        ]);
      }
      if (invoice.data.total.fee) {
        totals.table.body.push([
          {
            text: i18n.global.t("Fees"),
            noWrap: true,
          },
          getTotal("fee"),
        ]);
      }
      if (invoice.data.total.payments) {
        totals.table.body.push([
          {
            text: i18n.global.tc("Payment", 3),
            noWrap: true,
          },
          getTotal("payments", ["negative"]),
        ]);
      }
      // Late-payment finance charge — a positive charge that adds to the
      // balance, mirroring the credit-card fee line above.
      if (invoice.data.total.financeCharge) {
        totals.table.body.push([
          {
            text: i18n.global.t("Finance Charge"),
            noWrap: true,
          },
          getTotal("financeCharge"),
        ]);
      }
      totals.table.body.push([
        { text: i18n.global.t("Balance"), style: { fontSize: 11.5 } },
        getTotal("balance"),
      ]);
    }

    // Items
    const renderItems = (items, category) => {
      let headerRows = 1;
      const body = [
        [
          i18n.global.t("Line"),
          i18n.global.t("Description"),
          i18n.global.tc("Item"),
        ],
        ...(invoice.aia
          ? [
              [
                {
                  text: 0,
                  style: "lineItemNumber",
                },
                {
                  text: invoice.description,
                  style: "itemDescription",
                },
                {
                  stack: [
                    { text: i18n.global.t("Price"), style: "caption" },
                    {
                      text: invoice.total.subtotal,
                      style: ["caption", "bold", "right"],
                    },
                  ],
                },
              ],
            ]
          : items.map((item, i) => renderItem(item, i))),
      ];
      if (category) {
        // headerRows += 1;
        body.unshift([
          { text: category, style: "h6", colSpan: 2 },
          "",
          {
            stack: ["Category Total", getTotal("categories." + category)],
            style: "right",
          },
        ]);
      }
      return this.fitOversizedRows({
        table: {
          headerRows,
          body,
          widths: ["auto", "*", "auto"],
          dontBreakRows: true,
          keepWithHeaderRows: headerRows,
        },
        layout: {
          hLineWidth: (i, node) => {
            if (!i || i === node.table.body.length) {
              return 0;
            }
            return 1;
          },
          vLineWidth: () => 0,
          hLineColor: () => this.borderColor,
          paddingLeft: (i) => !i * 12,
          paddingRight: (i) => 12,
        },
      });
    };

    const renderItem = (item, i) => {
      let description;
      if (item.itemType === "material") {
        description = item.location || "";
        description =
          (description ? description + "\n" : "") +
          (options.removeLineBreaks
            ? item.data.description.replace(/\r?\n\s*/g, " ")
            : item.data.description);
      } else if (item.itemType === "labor") {
        description = `${i18n.global.t("Labor")} – ${item.type.label}`;
        if (item.data.description) {
          description +=
            "\n" +
            (options.removeLineBreaks
              ? item.data.description.replace(/\r?\n\s*/g, " ")
              : item.data.description);
        }
      } else if (item.itemType === "discount") {
        description = `${i18n.global.t("Discount")} (${item.value}): ${
          options.removeLineBreaks
            ? item.data.description.replace(/\r?\n\s*/g, " ")
            : item.data.description
        }`;
      } else if (item.itemType === "misc") {
        description = options.removeLineBreaks
          ? item.data.description.replace(/\r?\n\s*/g, " ")
          : item.data.description;
      }
      const quantity = item.quantity;
      const line = item.lineNumber || 0;
      const output = [
        {
          text: line,
          style: "lineItemNumber",
        },
        {
          text: description,
          style: "itemDescription",
        },
        { stack: [] },
      ];
      const stack = output[2].stack;
      if (item.image) {
        if (this.isSvgImage(item, this.doc.images[item.id])) {
          let svg = window.atob(this.doc.images[item.id].slice(26));
          stack.push({
            svg,
            width: item.image.width / 2,
            alignment: "center",
          });
        } else {
          stack.push({
            image: String(item.id),
            width: 100,
            alignment: "center",
          });
        }
      }
      const title = [];
      const body = [];
      if (item.itemType === "material") {
        title.push(item.brand, item.series);
        body.push([
          { text: i18n.global.t("Qty"), style: "caption" },
          { text: item.quantity, style: ["caption", "bold", "right"] },
        ]);
      } else if (item.itemType === "labor") {
        title.push(i18n.global.t("Labor"));
        if (item.quantity) {
          body.push([
            { text: i18n.global.tc("Item", 100), style: "caption" },
            { text: item.quantity, style: ["caption", "bold", "right"] },
          ]);
        }
      } else if (item.itemType === "discount") {
        title.push(i18n.global.t("Discount"));
      } else if (item.itemType === "misc") {
        if (item.brand) {
          title.push(item.brand);
        }
        title.push(i18n.global.tc("Other Charge"));
      }
      if (options.itemPrices) {
        if (item.itemType === "discount") {
          const total = invoice.data.total;
          const discountAmount = formatCurrency(
            item.data.type === "fixed"
              ? item.data.value
              : (item.data.value * total.subtotal) / 100
          );
          body.push([
            { text: i18n.global.t("Amount"), style: "caption" },
            {
              text: "−" + discountAmount,
              style: ["caption", "bold", "right", "negative"],
            },
          ]);
        } else {
          body.push([
            { text: i18n.global.t("Price"), style: "caption" },
            {
              text: item.price,
              style: ["caption", "bold", "right"],
            },
          ]);
        }
        if (item.itemType === "material" && quantity > 1) {
          body.push([
            { text: i18n.global.tc("Total"), style: "caption" },
            {
              text: item.total,
              style: ["caption", "bold", "right"],
            },
          ]);
        }
      }
      if (item.notax) {
        body.push([
          {
            text: i18n.global.t("Non-Taxable").toUpperCase(),
            style: ["caption", "bold", "right"],
            colSpan: 2,
          },
          {},
        ]);
      }
      stack.push(
        {
          stack: title,
          style: "bold",
        },
        body.length
          ? {
              table: {
                headerRows: 0,
                widths: ["*", "*"],
                body,
              },
              layout: {
                hLineWidth: () => 0,
                vLineWidth: () => 0,
                paddingTop: () => 0,
                paddingBottom: () => 0,
                paddingLeft: () => 0,
                paddingRight: () => 0,
              },
            }
          : null
      );
      return output;
    };

    const items = {
      stack: [],
      absolutePosition: { x: this.page.l, y: this.page.fold },
    };
    if (categories) {
      forEach(categories, (categoryItems, category) => {
        items.stack.push(renderItems(categoryItems, category));
      });
    } else {
      items.stack.push(renderItems(invoice.items));
    }

    // Totals
    items.stack.push(this.hr({ lineWidth: 2 }), {
      table: {
        headerRows: 0,
        widths: ["*", "auto"],
        dontBreakRows: true,
        body: [
          [
            {
              text: i18n.global.tc("Total", 3),
              style: "h6",
              width: "auto",
            },
            totals,
          ],
        ],
      },
      layout: {
        hLineWidth: () => 0,
        vLineWidth: () => 0,
        paddingLeft: () => 12,
        paddingRight: () => 0,
        paddingTop: () => 8,
        paddingBottom: () => 0,
      },
    });

    this.draw(items);
  }
}
